import {Box, Button, CircularProgress, TextField, Typography} from '@mui/material';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

import React, {ReactElement} from 'react';
import {singularizeCamelCase} from '../../utils/utils';
import {ResourceType} from '../../@types/resource-type';

export interface StrictDeleteModalProps {
    open: boolean;
    resourceType: ResourceType;
    resourceName: string;
    subResources: string;
    additionalWarning?: string;
    actionLabel?: string;
    isDeleting?: boolean;
    progressMessage?: string;
    onClose: () => void;
    handleDelete: () => void;
}
export default function StrictDeleteModal(props: StrictDeleteModalProps): ReactElement {
    const { open, resourceType, resourceName, subResources, additionalWarning, actionLabel = 'Delete', isDeleting = false, progressMessage, handleDelete, onClose } = props;
    const [text, setText] = React.useState('');
    const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setText(event.target.value);
    };

    return (
        <Dialog open={open} onClose={isDeleting ? undefined : onClose}>
            <DialogTitle>{actionLabel} {singularizeCamelCase(resourceType)} "{resourceName}" </DialogTitle>
            <DialogContent dividers>
                {isDeleting ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={48} />
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            {progressMessage || `${actionLabel === 'Archive' ? 'Archiving' : 'Deleting'} ${resourceName}...`}
                        </Typography>
                        {progressMessage && progressMessage.includes('cancel') && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Please wait while the job is being cancelled. This may take up to 30 seconds.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <>
                        <DialogContentText color='red'>
                            You are going to {actionLabel.toLowerCase()} {resourceName} and all {subResources}. 
                            This CANNOT be restored! Are you ABSOLUTELY sure?
                        </DialogContentText>
                        {additionalWarning && (
                            <DialogContentText color='warning' sx={{ mt: 2, fontWeight: 'bold' }}>
                                ⚠️ {additionalWarning}
                            </DialogContentText>
                        )}
                        <DialogContentText sx={{ mt: 2 }}>
                            To prevent accidental actions, please confirm your intention by typing the following:
                        </DialogContentText>
                        <br/>
                        <Typography variant='caption' color='red'>
                            <Chip label={resourceName} color='error' variant='outlined'/>
                        </Typography>

                        <TextField
                            autoFocus
                            margin='dense'
                            id='confirm-text-input'
                            variant='filled'
                            size='small'
                            onChange={onTextChange}
                            fullWidth
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {!isDeleting && (
                    <Button id='delete-resource' size='small' variant='outlined' color='error' onClick={handleDelete} disabled={resourceName !== text}>{actionLabel}</Button>
                )}
            </DialogActions>

        </Dialog>
    );
}
